import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  TextField,
  Typography,
  Box,
  Chip,
  CircularProgress,
} from "@mui/material";
import RequiredLabel from "./RequiredLabel";

const FreeSoloMultiSelect = ({
  label = "選択",
  inputLabel,
  name,
  value = [],
  onChange,
  options,
  endpoint,
  isRequired = false,
  error = false,
  helperText = "候補から選ぶか、入力して Enter で追加",
  max = Infinity,
  freeSolo = true,
  placeholder = "選択または入力",
  noOptionsText = "該当する候補はありません",
  fetchErrorText = "候補の取得に失敗しました",
  optionLabelKey = "name",
  optionValueKey = "id",
}) => {
  const [rawOptions, setRawOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [open, setOpen] = useState(false);

  // options / endpoint から候補をロード
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (Array.isArray(options)) {
        setRawOptions(options);
        return;
      }
      if (endpoint) {
        setLoading(true);
        try {
          const res = await fetch(endpoint);
          const data = await res.json();
          if (mounted) setRawOptions(Array.isArray(data) ? data : []);
        } catch (err) {
          if (mounted) {
            console.error("候補取得エラー:", err);
            setFetchError(err);
            setRawOptions([]);
          }
        } finally {
          mounted && setLoading(false);
        }
      }
    })();
    return () => {
      mounted = false;
    };
  }, [options, endpoint]);

  // 正規化
  const normalizedOptions = useMemo(
    () =>
      (rawOptions || []).map((opt) => ({
        id: opt?.[optionValueKey] ?? null,
        name: String(opt?.[optionLabelKey] ?? ""),
      })),
    [rawOptions, optionLabelKey, optionValueKey],
  );

  const normalizeName = (s) =>
    String(s ?? "")
      .trim()
      .replace(/\s+/g, " ");
  const existsByName = (items, name) =>
    items.some((i) => i.name.toLowerCase() === name.toLowerCase());

  const handleChange = (_e, newValue) => {
    const merged = (newValue || [])
      .map((v) =>
        typeof v === "string"
          ? { id: null, name: normalizeName(v) }
          : { id: v?.id ?? null, name: normalizeName(v?.name) },
      )
      .filter((v) => v && v.name);

    const deduped = [];
    merged.forEach((item) => {
      if (!existsByName(deduped, item.name)) deduped.push(item);
    });

    if (deduped.length <= max) onChange(deduped);
  };

  const handleKeyDown = (e) => {
    if (!freeSolo) return;
    if (
      e.key === "Enter" &&
      !e.nativeEvent.isComposing &&
      inputValue.trim() !== ""
    ) {
      e.preventDefault();
      const nameText = normalizeName(inputValue);
      if (value.length < max && !existsByName(value, nameText)) {
        onChange([...value, { id: null, name: nameText }]);
      }
      setInputValue("");
      setOpen(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <Typography>{label}</Typography>
        <RequiredLabel show={isRequired} />
      </Box>

      <Autocomplete
        multiple
        freeSolo={freeSolo}
        blurOnSelect={false}
        filterSelectedOptions
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        options={normalizedOptions}
        value={value}
        inputValue={inputValue}
        onInputChange={(_, v, reason) => {
          if (!freeSolo) return;
          setInputValue(v);
          if (reason === "input") setOpen(!!v);
        }}
        filterOptions={freeSolo ? undefined : (options) => options}
        onChange={(e, newValue, reason) => {
          handleChange(e, newValue);
          if (reason === "selectOption" || reason === "createOption")
            setOpen(false);
        }}
        isOptionEqualToValue={(opt, val) => {
          if (!opt || !val) return false;

          if (opt.id != null && val.id != null) {
            return opt.id === val.id;
          }

          const optName = String(opt.name || "").toLowerCase();
          const valName = String(val.name || "").toLowerCase();

          return optName === valName;
        }}
        getOptionLabel={(opt) =>
          typeof opt === "string" ? opt : opt?.name || ""
        }
        noOptionsText={fetchError ? fetchErrorText : noOptionsText}
        loading={loading}
        loadingText={
          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <CircularProgress size={16} /> 読み込み中…
          </span>
        }
        ListboxProps={{
          sx: {
            "& .MuiAutocomplete-option": {
              fontSize: { xs: "0.9rem", sm: "1rem" },
              whiteSpace: "normal",
              wordBreak: "break-word",
            },
          },
        }}
        renderTags={(vals, getTagProps) =>
          vals.map((item, index) => (
            <Chip
              key={`${item.id ?? "manual"}-${item.name}-${index}`}
              label={item.name}
              {...getTagProps({ index })}
              sx={{
                backgroundColor: "#4F46E5",
                color: "white",
                "& .MuiChip-deleteIcon": {
                  color: "white",
                  "&:hover": { color: "#f0f0f0" },
                },
              }}
            />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            name={name}
            label={inputLabel}
            placeholder={placeholder}
            error={!!error}
            helperText={helperText}
            onKeyDown={handleKeyDown}
            InputProps={{
              ...params.InputProps,
              readOnly: !freeSolo,
            }}
            sx={{
              "& .MuiInputBase-input:focus": { boxShadow: "none" },
            }}
          />
        )}
      />
    </Box>
  );
};

export default FreeSoloMultiSelect;
